/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import {
  type UseMutationResult,
  type UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import { type Notification, notificationsApi } from '../api/notifications.js';

const KEY = ['notifications'] as const;

function useInvalidate(): () => void {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: KEY });
  };
}

export function useNotifications(): UseQueryResult<readonly Notification[]> {
  return useQuery({ queryKey: [...KEY, 'list'], queryFn: () => notificationsApi.list() });
}

/** Unread badge count; polled every 60s for near-real-time. */
export function useUnreadCount(): UseQueryResult<number> {
  return useQuery({
    queryKey: [...KEY, 'unread'],
    queryFn: () => notificationsApi.unreadCount(),
    refetchInterval: 60_000,
  });
}

export function useMarkNotificationRead(): UseMutationResult<void, Error, string> {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (id: string) => notificationsApi.markRead(id),
    onSuccess: invalidate,
  });
}

export function useMarkAllNotificationsRead(): UseMutationResult<void, Error, void> {
  const invalidate = useInvalidate();
  return useMutation({ mutationFn: () => notificationsApi.markAllRead(), onSuccess: invalidate });
}
